import React, { useState, useRef, useEffect } from 'react';
import { ExportButton, useNotify } from 'react-admin';
import { Box, LinearProgress, Typography } from '@mui/material';
import { serverApiUrl } from '../../config/constants';
import transformURL from '../../helpers/transformURL';

const downloadFile = (downloadUrl, fileName = 'reports') => {
  const link = document.createElement('a');
  link.setAttribute('href', downloadUrl);
  link.setAttribute('download', fileName);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
  document.body.removeChild(link);
};

const CustomExportButton = ({ filter, path = window.location.pathname, sort, filterValues, resource }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    (() => {})();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const handleClick = async () => {
    try {
      setLoading(true);
      setProgress(null);

      const routePath = path.slice(1);
      const url = transformURL(serverApiUrl, `${routePath}/xlsx`, filter);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        notify('reports.error.download', 'warning');
        return;
      }

      const contentType = response.headers.get('content-type');
      const contentDisposition = response.headers.get('content-disposition');
      const jobId = response.headers.get('x-job-id');
      const isAttachment = contentDisposition && contentDisposition.indexOf('attachment') !== -1;

      let fileName = 'export.xlsx';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      if (
        isAttachment &&
        contentType &&
        (contentType.indexOf('spreadsheetml') !== -1 || contentType.indexOf('csv') !== -1)
      ) {
        if (jobId) {
          const progressUrl = transformURL(serverApiUrl, `admin/reports/export-progress/${jobId}`, '');
          const eventSource = new EventSource(progressUrl);
          eventSourceRef.current = eventSource;

          // eventSource.onopen = () => {
          //   console.log('SSE connection opened');
          // };

          eventSource.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);

              setProgress({
                totalRows: parseInt(data.total, 10),
                currentRows: parseInt(data.current, 10),
                progress: data.total > 0 ? Math.round((data.current / data.total) * 100) : 0,
              });
            } catch (e) {
              console.error('SSE parse error:', e, event.data);
            }
          };

          eventSource.onerror = (error) => {
            console.error('SSE error:', error);
            eventSource.close();
            eventSourceRef.current = null;
          };
        }

        if (!response.body) {
          const blob = await response.blob();
          const downloadUrl = URL.createObjectURL(blob);
          downloadFile(downloadUrl, fileName);
          notify('Export completed', 'success');

          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          return;
        }

        // ReadableStream olvasása
        const reader = response.body.getReader();
        const chunks = [];
        const run = true;

        while (run) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
        }

        const blob = new Blob(chunks, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const downloadUrl = URL.createObjectURL(blob);
        downloadFile(downloadUrl, fileName);

        notify('Export completed successfully', 'success');

        // SSE cleanup
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        setTimeout(() => {
          setProgress(null);
        }, 1500);

        return;
      }

      // EGYÉB esetek (ZIP, JSON URL, stb.)
      let downloadUrl;

      if (isAttachment) {
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
      } else if (contentType && contentType.indexOf('application/zip') !== -1) {
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
        fileName = 'export.zip';
      } else {
        const json = await response.json();
        downloadUrl = json.url;
        fileName = json.fileName || fileName;
      }

      downloadFile(downloadUrl, fileName);
      notify('Export completed', 'success');
    } catch (error) {
      console.error('Export error:', error);
      notify('reports.error.download', 'error');

      // SSE cleanup error esetén is
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <ExportButton
        exporter={handleClick}
        maxResults={0}
        sort={sort}
        filterValues={filterValues}
        resource={resource}
        disabled={loading}
      />

      {loading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant={progress && progress.progress !== undefined ? 'determinate' : 'indeterminate'}
            value={progress?.progress || 0}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {progress && progress.progress !== undefined && `${progress.progress}% • `}
            {progress && progress.totalRows && progress.currentRows && progress.totalRows > 0
              ? `${progress.currentRows.toLocaleString()} / ${progress.totalRows.toLocaleString()} rows`
              : 'Preparing export...'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CustomExportButton;
